/**
 * Music manifest — maps stages, bosses, and UI scenes to audio track keys.
 *
 * All tracks are .ogg files in public/assets/music/ (see MUSIC_SPEC.md).
 * Keys define the shape; assets are generated separately via Suno.
 * Until assets land, MusicDirectorAdapter silently falls back to NoopHowl.
 *
 * Naming convention:
 *   music_s{N}_m{M}  — stage N, movement M (1-indexed)
 *   music_boss        — shared boss battle track (B1)
 *   music_boss_s9     — S9-specific boss track (B2)
 *   music_menu        — title screen (U1)
 *   music_charselect  — character select (U2)
 *   music_victory     — victory sting / credits (U3)
 *   music_gameover    — game over (U4)
 */

export type MusicKey = string;

/** Base path for all music .ogg files (relative to public/). */
const MUSIC_DIR = 'assets/music/';

/** Build the file path for a music key. */
export function musicPath(key: MusicKey): string {
  return `${MUSIC_DIR}${key}.ogg`;
}

export const MusicKeys = {
  // Stage movements — three per stage; Movement I is always present.
  // Movements II/III fall back to I when the .ogg is absent (see MusicDirector).
  S1_M1: 'music_s1_m1',
  S1_M2: 'music_s1_m2',
  S1_M3: 'music_s1_m3',
  S2_M1: 'music_s2_m1',
  S2_M2: 'music_s2_m2',
  S2_M3: 'music_s2_m3',
  S3_M1: 'music_s3_m1',
  S3_M2: 'music_s3_m2',
  S3_M3: 'music_s3_m3',
  S4_M1: 'music_s4_m1',
  S4_M2: 'music_s4_m2',
  S4_M3: 'music_s4_m3',
  S5_M1: 'music_s5_m1',
  S5_M2: 'music_s5_m2',
  S5_M3: 'music_s5_m3',
  S6_M1: 'music_s6_m1',
  S6_M2: 'music_s6_m2',
  S6_M3: 'music_s6_m3',
  S7_M1: 'music_s7_m1',
  S7_M2: 'music_s7_m2',
  S7_M3: 'music_s7_m3',
  S8_M1: 'music_s8_m1',
  S8_M2: 'music_s8_m2',
  S8_M3: 'music_s8_m3',
  S9_M1: 'music_s9_m1',
  S9_M2: 'music_s9_m2',
  S9_M3: 'music_s9_m3',
  // Boss tracks
  BOSS_SHARED: 'music_boss', // B1 — stages 1-8
  BOSS_S9: 'music_boss_s9', // B2 — Whitehall / Captain Vlad
  BOSS_PRAETOR: 'music_boss_praetor', // B2 "The Establishment" — The Praetor (S9 Phase 3)
  // UI tracks
  MENU: 'music_menu',
  CHAR_SELECT: 'music_charselect',
  VICTORY: 'music_victory',
  GAME_OVER: 'music_gameover',
} as const;

/** All defined music keys, for preloading or audit. */
export const ALL_MUSIC_KEYS: readonly MusicKey[] = Object.values(MusicKeys);

/** UI scene contexts that drive music selection. */
export type UIScene = 'menu' | 'charSelect' | 'gameOver' | 'victory';

/**
 * Stage movement list — at least one key (Movement I).
 * MusicDirector clamps the index to the last entry when a checkpoint would
 * exceed the list length, giving "hold last available" fallback behaviour.
 */
export type MovementList = [MusicKey, ...MusicKey[]];

/** The full music manifest consumed by MusicDirector. */
export interface MusicManifest {
  /** Stage number (1–9) → ordered movement keys. */
  stages: Readonly<Record<number, MovementList>>;
  /** bossId → specific track key (overrides sharedBossKey when present). */
  bossTracks: Readonly<Record<string, MusicKey>>;
  /** Fallback boss track when bossId has no specific entry. */
  sharedBossKey: MusicKey;
  /** UI scene → track key. */
  uiTracks: Readonly<Record<UIScene, MusicKey>>;
}

export const MUSIC_MANIFEST: MusicManifest = {
  stages: {
    1: [MusicKeys.S1_M1, MusicKeys.S1_M2, MusicKeys.S1_M3],
    2: [MusicKeys.S2_M1, MusicKeys.S2_M2, MusicKeys.S2_M3],
    3: [MusicKeys.S3_M1, MusicKeys.S3_M2, MusicKeys.S3_M3],
    4: [MusicKeys.S4_M1, MusicKeys.S4_M2, MusicKeys.S4_M3],
    5: [MusicKeys.S5_M1, MusicKeys.S5_M2, MusicKeys.S5_M3],
    6: [MusicKeys.S6_M1, MusicKeys.S6_M2, MusicKeys.S6_M3],
    7: [MusicKeys.S7_M1, MusicKeys.S7_M2, MusicKeys.S7_M3],
    8: [MusicKeys.S8_M1, MusicKeys.S8_M2, MusicKeys.S8_M3],
    9: [MusicKeys.S9_M1, MusicKeys.S9_M2, MusicKeys.S9_M3],
  },
  bossTracks: {
    // captain_vlad uses the S9-specific boss track (B2); all other bosses get B1.
    captain_vlad: MusicKeys.BOSS_S9,
  },
  sharedBossKey: MusicKeys.BOSS_SHARED,
  uiTracks: {
    menu: MusicKeys.MENU,
    charSelect: MusicKeys.CHAR_SELECT,
    victory: MusicKeys.VICTORY,
    gameOver: MusicKeys.GAME_OVER,
  },
};

/**
 * Resolve the boss track key for a given bossId.
 * Falls back to the shared boss key if no specific mapping exists.
 */
export function resolveBossTrack(manifest: MusicManifest, bossId: string): MusicKey {
  return manifest.bossTracks[bossId] ?? manifest.sharedBossKey;
}

/**
 * Resolve a stage movement key for the given stageId and movementIndex.
 * Clamps movementIndex to the last available movement — never returns null
 * when the stage has an entry. Returns null when the stage has no manifest entry.
 */
export function resolveMovement(
  manifest: MusicManifest,
  stageId: number,
  movementIndex: number,
): MusicKey | null {
  const movements = manifest.stages[stageId];
  if (movements === undefined) return null;
  const idx = Math.min(Math.max(0, movementIndex), movements.length - 1);
  return movements[idx];
}
